//
//  SDKBorderToken.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { SupernovaError } from "../../core/errors/SDKSupernovaError";
import { ALL_BORDER_POSITIONS, BorderPosition, } from "../enums/SDKBorderPosition";
import { ALL_BORDER_STYLES, BorderStyle } from "../enums/SDKBorderStyle";
import { TokenType } from "../enums/SDKTokenType";
import { ColorToken } from "./SDKColorToken";
import { DimensionToken } from "./SDKDimensionToken";
import { Token } from "./SDKToken";
import { v4 as uuidv4 } from "uuid";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: -  Object Definition
export class BorderToken extends Token {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(versionId, baseToken, value, alias, properties, propertyValues) {
        super(baseToken, versionId, properties, propertyValues);
        this.tokenType = TokenType.border;
        this.value = value;
        if (alias) {
            this.value.referencedTokenId = alias.id;
        }
    }
    static create(versionId, brandId, name, description, value, alias, referenceResolver, properties, propertyValues) {
        const baseToken = {
            // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
            id: undefined, // Ommited id will create new token
            persistentId: uuidv4(),
            brandId,
            designSystemVersionId: versionId,
            type: TokenType.border,
            meta: {
                name,
                description,
            },
            data: {},
            customPropertyOverrides: [],
        };
        if (value !== null && value !== undefined) {
            // Raw value
            const tokenValue = this.borderValueFromDefinition(value, referenceResolver);
            return new BorderToken(versionId, baseToken, tokenValue, 
            // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
            undefined, properties, propertyValues);
        }
        if (alias) {
            // Aliased value - copy and create raw from reference
            const tokenValue = {
                color: alias.value.color,
                width: alias.value.width,
                position: alias.value.position,
                style: alias.value.style,
                referencedTokenId: alias.id,
            };
            return new BorderToken(versionId, baseToken, tokenValue, 
            // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
            undefined, properties, propertyValues);
        }
        throw SupernovaError.fromMessage("Border Token must be created using value or alias, but none was provided");
    }
    static borderValueFromDefinition(definition, referenceResolver) {
        let data = definition;
        // For now, handle only one shadow in multiple shadow layers
        if (data instanceof Array) {
            if (data.length > 0) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                data = data[0];
            }
            else {
                // Empty definition needs to fallback to proper SN definition - make it transparent shadow with 0 0 0 0 values
                data = {
                    color: "rgba(0,0,0,0)",
                    position: "outside",
                    style: "solid",
                    width: 0,
                    type: "border",
                };
            }
        }
        if (
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line no-prototype-builtins
        !definition.hasOwnProperty("color") || // TODO:fix-sdk-eslint
            // eslint-disable-next-line no-prototype-builtins
            !definition.hasOwnProperty("width")) {
            throw SupernovaError.fromMessage(`Border definition is missing one of required properties (color, width), was ${JSON.stringify(definition)}`);
        }
        const value = {}; // Empty container
        // @ts-expect-error TS(2322): Type 'ColorTokenValue | undefined' is not assignab... Remove this comment to see the full error message
        value.color = ColorToken.colorValueFromDefinitionOrReference(
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        definition.color, referenceResolver);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        value.position = this.toBorderPosition(definition.position);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        value.style = this.toBorderStyle(definition.style);
        value.width = DimensionToken.dimensionValueFromDefinitionOrReference(
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        definition.width, referenceResolver, TokenType.borderWidth);
        // TODO: Position, style
        if (value.color === undefined) {
            throw new Error(`Unable to resolve value 'color' for border token definition \n${JSON.stringify(definition, null, 2)}\n Did you possibly use incorrect reference?`);
        }
        if (value.position === undefined) {
            throw new Error(`Unable to resolve value 'position' for border token definition \n${JSON.stringify(definition, null, 2)}\n Did you possibly use incorrect reference?`);
        }
        if (value.width === undefined) {
            throw new Error(`Unable to resolve value 'width' for border token definition \n${JSON.stringify(definition, null, 2)}\n Did you possibly use incorrect reference?`);
        }
        return value;
    }
    static toBorderStyle(value) {
        var _a;
        return (
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        (_a = ALL_BORDER_STYLES.find((v) => v.toLowerCase() === (value === null || value === void 0 ? void 0 : value.toLowerCase()))) !== null && _a !== void 0 ? _a : BorderStyle.solid);
    }
    static toBorderPosition(value) {
        var _a;
        return ((_a = ALL_BORDER_POSITIONS.find(
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        (v) => v.toLowerCase() === (value === null || value === void 0 ? void 0 : value.toLowerCase()))) !== null && _a !== void 0 ? _a : BorderPosition.outside);
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Writing
    toWriteObject() {
        const baseData = this.toBaseWriteObject();
        const specificData = baseData;
        specificData.data = BorderToken.valueToWriteObject(this.value);
        return specificData;
    }
    static valueToWriteObject(value) {
        var _a, _b, _c, _d;
        const valueObject = !value.referencedTokenId
            ? {
                color: {
                    aliasTo: (_a = value.color.referencedTokenId) !== null && _a !== void 0 ? _a : undefined,
                    value: value.color.referencedTokenId
                        ? null
                        : {
                            color: ColorToken.colorValueToHex8(value.color),
                            opacity: {
                                aliasTo: (_b = value.color.opacity.referencedTokenId) !== null && _b !== void 0 ? _b : undefined,
                                value: value.color.opacity.referencedTokenId
                                    ? null
                                    : {
                                        measure: value.color.opacity.measure,
                                        unit: value.color.opacity.unit,
                                    },
                            },
                        },
                },
                width: {
                    aliasTo: (_c = value.width.referencedTokenId) !== null && _c !== void 0 ? _c : undefined,
                    value: value.width.referencedTokenId
                        ? null
                        : {
                            measure: value.width.measure,
                            unit: value.width.unit,
                        },
                },
                position: value.position,
                style: value.style,
            }
            : undefined;
        return {
            aliasTo: (_d = value.referencedTokenId) !== null && _d !== void 0 ? _d : undefined,
            // @ts-expect-error TS(2322): Type '{ color: { aliasTo: string | undefined; valu... Remove this comment to see the full error message
            value: valueObject,
        };
    }
}
